import React from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { tailwindColors } from '@cineamo/ui/src/helpers/tailwind-config-helper';

import Checkbox from '@cineamo/ui/src/checkbox/Checkbox';
import { IconCheckedSolid24 } from '@cineamo/ui/src/icons/ic-checkedSolid';
import { IconUnchecked24 } from '@cineamo/ui/src/icons/ic-unchecked';

type FormCheckboxProps = {
    /** Doubles as the input's `id`, so the label is click-/screen-reader-associated with it. */
    name: string;
    label: React.ReactNode;
    checked: boolean;
    onChange: (checked: boolean) => void;
    required?: boolean;
    error?: string;
    /**
     * Which domain color pair the label and the unchecked box are drawn from. The form pages put
     * their forms on a surface card, the newsletter view renders straight on the page background,
     * and no ancestor sets a `color`, so `currentColor` cannot be relied on to resolve this.
     */
    on?: 'surface' | 'background';
    className?: string;
};

/**
 * The one checkbox for the cinema website's forms.
 *
 * Wraps the shared `ui` Checkbox instead of a native input or a third-party one, but replaces both
 * of its icons: the built-in pair is painted in the fixed cyan/darkBlue palette, which does not
 * belong on a domain-themed site. The checked box uses the domain primary with the paired
 * on-primary tick, so the mark stays visible even on cinemas whose primary IS white (#FFFFFF).
 */
export default function FormCheckbox({
    name,
    label,
    checked,
    onChange,
    required = false,
    error,
    on = 'surface',
    className
}: FormCheckboxProps) {
    const textColor = on === 'background' ? 'text-[var(--on-background-color)]' : 'text-[var(--on-surface-color)]';
    const uncheckedFill =
        on === 'background' ? 'rgba(var(--on-background-color-rgb),0.3)' : 'rgba(var(--on-surface-color-rgb),0.3)';

    return (
        <div className={cn('flex flex-col gap-4', textColor, className)}>
            <div className="flex items-start gap-8">
                <Checkbox
                    id={name}
                    className="shrink-0"
                    checked={checked}
                    onChange={onChange}
                    customIcons={{
                        checked: (
                            <IconCheckedSolid24
                                className="size-24 rounded-4"
                                fill="var(--primary-color)"
                                foregroundcolor="var(--on-primary-color)"
                            />
                        ),
                        unchecked: (
                            <IconUnchecked24
                                className="size-24"
                                fill={error ? tailwindColors.red.DEFAULT : uncheckedFill}
                            />
                        )
                    }}
                />
                <label
                    htmlFor={name}
                    className="text-body-14 cursor-pointer">
                    {label}
                    {required && <span className="ml-4 text-red">*</span>}
                </label>
            </div>
            {error && <p className="text-body-12 ml-32 text-red">{error}</p>}
        </div>
    );
}
